import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { LogOut, Inbox } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-sand/60 bg-white px-4 py-3 text-sm text-espresso focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20";

export default function AdminPage() {
  const { t } = useLang();
  const a = t.admin;
  const [token, setToken] = useState(() => sessionStorage.getItem("dci_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginState, setLoginState] = useState("idle");
  const [inquiries, setInquiries] = useState(null);
  const [error, setError] = useState("");
  const [fService, setFService] = useState("");
  const [fLang, setFLang] = useState("");
  const [fStatus, setFStatus] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setLoginState("sending");
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem("dci_admin_token", data.token);
      setToken(data.token);
    } catch {
      setError(a.loginError);
      setLoginState("idle");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("dci_admin_token");
    setToken("");
    setInquiries(null);
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/inquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setInquiries(data.inquiries))
      .catch(() => {
        setError(a.loadError);
        sessionStorage.removeItem("dci_admin_token");
        setToken("");
      });
  }, [token, a.loadError]);

  const setStatus = async (id, status) => {
    await axios.patch(
      `${API}/inquiries/${id}/status`,
      { status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setInquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const filtered = useMemo(() => {
    if (!inquiries) return [];
    return inquiries.filter(
      (q) =>
        (!fService || q.service === fService) &&
        (!fLang || q.preferred_language === fLang) &&
        (!fStatus || q.status === fStatus)
    );
  }, [inquiries, fService, fLang, fStatus]);

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-cream grain px-4 pt-20" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-md rounded-2xl border border-sand/50 bg-white p-8 shadow-lg" data-testid="admin-login-form">
          <h1 className="font-serif text-2xl font-medium text-espresso">{a.loginTitle}</h1>
          <div className="mt-6 space-y-4">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm font-semibold text-espresso">{a.email}</label>
              <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-espresso">{a.password}</label>
              <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" />
            </div>
          </div>
          {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-800" role="alert" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loginState === "sending"}
            className="mt-6 w-full rounded-full bg-espresso px-6 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-terracotta active:scale-95 disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loginState === "sending" ? a.signingIn : a.signIn}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream pt-28 pb-20" data-testid="admin-inbox-page">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-serif text-3xl font-semibold text-espresso">{a.title}</h1>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-espresso/25 px-5 py-2.5 text-sm font-semibold text-espresso hover:bg-espresso hover:text-cream transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" /> {a.logout}
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-filters">
          <select value={fService} onChange={(e) => setFService(e.target.value)} className="rounded-full border border-sand/60 bg-white px-4 py-2 text-sm" data-testid="admin-filter-service" aria-label={a.filters.all}>
            <option value="">{a.filters.all}</option>
            {Object.entries(t.contact.form.services).map(([slug, label]) => (
              <option key={slug} value={slug}>{label}</option>
            ))}
          </select>
          <select value={fLang} onChange={(e) => setFLang(e.target.value)} className="rounded-full border border-sand/60 bg-white px-4 py-2 text-sm" data-testid="admin-filter-language" aria-label={a.filters.allLang}>
            <option value="">{a.filters.allLang}</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="rounded-full border border-sand/60 bg-white px-4 py-2 text-sm" data-testid="admin-filter-status" aria-label={a.filters.allStatus}>
            <option value="">{a.filters.allStatus}</option>
            <option value="new">{a.status.new}</option>
            <option value="read">{a.status.read}</option>
            <option value="archived">{a.status.archived}</option>
          </select>
        </div>

        {!inquiries ? (
          <p className="mt-10 text-espresso/60">…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-sand/50 bg-white py-16 text-espresso/60" data-testid="admin-empty-state">
            <Inbox className="h-10 w-10 text-sand" />
            <p className="mt-4">{a.empty}</p>
          </div>
        ) : (
          <ul className="mt-6 space-y-4" data-testid="admin-inquiries-list">
            {filtered.map((q, idx) => (
              <li key={q.id} className="rounded-2xl border border-sand/50 bg-white p-6" data-testid={`admin-inquiry-row-${idx}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg font-medium text-espresso" data-testid={`admin-inquiry-name-${idx}`}>{q.full_name}</p>
                    <p className="mt-0.5 text-xs text-espresso/60">
                      {new Date(q.created_at).toLocaleString()} · {t.contact.form.services[q.service] || q.service} ·{" "}
                      {q.preferred_language.toUpperCase()}
                      {q.preferred_date ? ` · ${q.preferred_date} ${q.preferred_time || ""}` : ""}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      q.status === "new"
                        ? "bg-terracotta/15 text-terracotta"
                        : q.status === "read"
                          ? "bg-teal/15 text-teal"
                          : "bg-espresso/10 text-espresso/60"
                    }`}
                    data-testid={`admin-inquiry-status-${idx}`}
                  >
                    {a.status[q.status] || q.status}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-espresso/80">{q.message}</p>
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-espresso/75">
                  <span>{q.email}</span>
                  {q.phone && <span>{q.phone}</span>}
                  <span className="text-xs uppercase tracking-wide text-espresso/50">{q.contact_method}</span>
                </div>
                <div className="mt-4 flex gap-2">
                  {["new", "read", "archived"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(q.id, s)}
                      disabled={q.status === s}
                      data-testid={`admin-inquiry-mark-${s}-${idx}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                        q.status === s
                          ? "bg-espresso text-cream"
                          : "border border-espresso/20 text-espresso hover:bg-sand/30"
                      }`}
                    >
                      {a.status[s]}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
